import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { customers, type CustomerRow } from '@/api/customers';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useResellerContext } from '@/lib/reseller-context-store';
import { useAuth } from '@/lib/auth-store';
import {
  Plus, ArrowRight, Search, Sparkles, Check, LogIn, Building2, Settings2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';

const LANG_FLAG: Record<CustomerRow['language'], string> = {
  en: 'EN',
  he: 'HE',
  yi: 'YI',
};

const LANG_TONE: Record<CustomerRow['language'], string> = {
  en: 'bg-slate-100 text-slate-700',
  he: 'bg-blue-50 text-blue-700',
  yi: 'bg-amber-50 text-amber-800',
};

/**
 * Customers list — the "portal lobby" for super_admin and reseller_owner.
 *
 * This page is the entry point to every customer-scoped view in the
 * portal. Clicking "Enter" on a row writes the customer into the
 * customer-context store (persisted to localStorage) and navigates to
 * the dashboard, where the rest of the portal then operates inside
 * that customer's scope.
 *
 * For non-admin roles we keep the existing behavior — they land on
 * /dashboard directly and manage their own tenant. If an office_manager
 * somehow lands here (edge case via direct URL), we still render the
 * list read-only so they at least see something useful.
 */
export function CustomerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const currentContext = useCustomerContext((s) => s.current);
  const enter = useCustomerContext((s) => s.enter);
  const resellerContext = useResellerContext((s) => s.current);

  const [search, setSearch] = useState('');

  // Super-admins who have entered a reseller see only that reseller's
  // customers. Reseller_owner is already scoped by the backend to their
  // own reseller, and the context store is irrelevant for them.
  const filterResellerId = user?.role === 'super_admin' ? resellerContext?.id : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', filterResellerId ?? 'all'],
    queryFn: () => customers.list({ limit: 500, resellerId: filterResellerId }),
  });

  const rows = data?.rows ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...rows].sort((a, b) => a.name.localeCompare(b.name));
    if (!q) return list;
    return list.filter((r) =>
      r.name.toLowerCase().includes(q)
      || String(r.customer_number).includes(q));
  }, [rows, search]);

  const activeCount = rows.filter((r) => r.active).length;
  const inactiveCount = rows.length - activeCount;

  const canEnter = user?.role === 'super_admin' || user?.role === 'reseller_owner';

  function handleEnter(row: CustomerRow) {
    enter({
      id: row.id,
      name: row.name,
      resellerId: row.reseller_id,
      customerNumber: row.customer_number,
    });
    // Navigate to the customer's scoped account page so the URL bar
    // reflects which customer the admin / reseller is now viewing.
    // Super_admin's URL includes both `/reseller/{N}/customer/{N}/...`;
    // reseller_owner's omits the reseller segment (they only manage
    // their own reseller).
    const isSuper = user?.is_super_admin;
    const resellerNumber = resellerContext?.resellerNumber;
    if (isSuper && resellerNumber != null && row.customer_number != null) {
      navigate(`/reseller/${resellerNumber}/customer/${row.customer_number}/account`);
    } else if (row.customer_number != null) {
      navigate(`/customer/${row.customer_number}/account`);
    } else {
      navigate('/dashboard');
    }
  }

  const filteringByReseller = Boolean(filterResellerId && resellerContext);

  return (
    <>
      <PageHeader
        eyebrow={filteringByReseller ? `Resellers › ${resellerContext!.name}` : t('nav.customers')}
        title={filteringByReseller ? `Customers of ${resellerContext!.name}` : t('customer.chooseCustomer')}
        description={
          filteringByReseller
            ? t('customer.resellerScopedCustomers')
            : canEnter
              ? t('customer.chooseCustomerDescription')
              : t('customer.resellerScopedReadOnly')
        }
        actions={
          <Button asChild>
            <Link to="/customers/new">
              <Plus className="h-4 w-4" /> {t('customer.newCustomer')}
            </Link>
          </Button>
        }
      />

      {filteringByReseller && (
        <div className="mb-5 flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 className="h-3.5 w-3.5 text-indigo-600" />
          <span>
            {t('customer.filteredToReseller')}{' '}
            <span className="font-semibold text-foreground" dir="auto">
              {resellerContext!.name}
            </span>
            {'. '}{t('customer.exitResellerBanner')}
          </span>
        </div>
      )}

      {/* Summary strip — editorial, tabular-num flavour to match Dashboard */}
      <section className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryTile label={t('customer.total')} value={rows.length} loading={isLoading} />
        <SummaryTile label={t('common.active')} value={activeCount} loading={isLoading} tone="emerald" />
        <SummaryTile label={t('common.inactive')} value={inactiveCount} loading={isLoading} tone="slate" />
        <SummaryTile
          label={t('customer.nowEntered')}
          value={currentContext?.name ?? '—'}
          loading={false}
          tone={currentContext ? 'primary' : 'slate'}
          textValue
        />
      </section>

      {/* Search bar */}
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('customer.searchPlaceholder')}
            className="pl-9"
            aria-label={t('customer.searchPlaceholder')}
          />
        </div>
        {search && (
          <p className="text-xs text-muted-foreground tabular-nums">
            {filtered.length} of {rows.length} match
          </p>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-20 rounded-lg border border-dashed border-border bg-secondary/20 animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {(error as Error).message}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasQuery={Boolean(search)} />
      ) : (
        <ul className="grid gap-2">
          {filtered.map((row) => (
            <CustomerRoster
              key={row.id}
              row={row}
              isEntered={currentContext?.id === row.id}
              canEnter={canEnter}
              onEnter={() => handleEnter(row)}
            />
          ))}
        </ul>
      )}
    </>
  );
}

// ── Roster row ────────────────────────────────────────────────────────────────
// A horizontal, editorial card with a clear Enter affordance. Designed to
// feel like a directory of accounts rather than a generic table.

function CustomerRoster({
  row,
  isEntered,
  canEnter,
  onEnter,
}: {
  row: CustomerRow;
  isEntered: boolean;
  canEnter: boolean;
  onEnter: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li
      className={cn(
        'group relative rounded-lg border bg-card transition-all',
        isEntered
          ? 'border-primary border-2 shadow-[0_0_0_4px_hsl(221.2_83.2%_53.3%/0.08)]'
          : 'border-border hover:border-slate-400 hover:shadow-sm',
      )}
    >
      <div className="flex items-center gap-4 px-4 py-3 sm:px-5 sm:py-4">
        {/* Customer number chip — editorial monospace */}
        <div
          className={cn(
            'flex-shrink-0 flex h-12 w-14 flex-col items-center justify-center rounded border-2 font-mono tabular-nums',
            isEntered
              ? 'border-primary bg-primary/5 text-primary'
              : 'border-slate-300 bg-slate-50 text-slate-600 group-hover:border-slate-900 group-hover:text-slate-900',
          )}
          aria-hidden
        >
          <span className="text-[9px] uppercase tracking-widest leading-none opacity-60">C</span>
          <span className="text-base font-bold leading-tight mt-0.5">{formatRef(row.customer_number)}</span>
        </div>

        {/* Name + meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/customers/${row.id}`}
              className="text-base font-semibold text-foreground hover:text-primary hover:underline decoration-dotted underline-offset-4 truncate"
            >
              {row.name}
            </Link>
            <span
              className={cn(
                'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wider leading-none',
                LANG_TONE[row.language],
              )}
            >
              {LANG_FLAG[row.language]}
            </span>
            {!row.active && (
              <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wider leading-none bg-slate-200 text-slate-600">
                {t('customer.inactive')}
              </span>
            )}
            {isEntered && (
              <span className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wider leading-none bg-primary text-primary-foreground">
                <Check className="h-2.5 w-2.5" /> {t('customer.youAreHere')}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
            {row.timezone && (
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3 opacity-60" />
                {row.timezone}
              </span>
            )}
            <span className="tabular-nums">
              {t('customer.joined')} {new Date(row.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Action cluster */}
        <div className="flex-shrink-0 flex items-center gap-2">
          {/* "Manage" = the customer's admin page: feature switches, limits,
              timezone, default country code. Named explicitly because
              resellers couldn't find where to change an existing account's
              features (Sam 2026-08-06). */}
          <Button
            asChild
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            <Link to={`/customers/${row.id}`}>
              <Settings2 className="h-3.5 w-3.5" />
              Manage
            </Link>
          </Button>
          {canEnter ? (
            <Button
              type="button"
              onClick={onEnter}
              disabled={!row.active}
              size="sm"
              variant={isEntered ? 'secondary' : 'default'}
              className="gap-1.5 min-w-[5.5rem] justify-between"
              aria-label={isEntered ? row.name : row.name}
            >
              {isEntered ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  {t('common.entered')}
                </>
              ) : (
                <>
                  <LogIn className="h-3.5 w-3.5" />
                  {t('common.enter')}
                  <ArrowRight className="h-3.5 w-3.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </Button>
          ) : (
            <Button asChild size="sm" variant="outline">
              <Link to={`/customers/${row.id}`}>
                {t('common.open')}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

// ── Summary tile ──────────────────────────────────────────────────────────────

function SummaryTile({
  label, value, loading, tone = 'slate', textValue = false,
}: {
  label: string;
  value: number | string;
  loading: boolean;
  tone?: 'slate' | 'primary' | 'emerald';
  textValue?: boolean;
}) {
  const toneClass = {
    slate: 'border-slate-200 text-slate-900',
    primary: 'border-primary/30 text-primary bg-primary/[0.04]',
    emerald: 'border-emerald-200 text-emerald-900',
  }[tone];
  return (
    <div className={cn('rounded-lg border p-3 bg-card', toneClass)}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          'mt-1 font-mono tabular-nums leading-none truncate',
          textValue ? 'text-sm font-semibold' : 'text-2xl font-bold',
        )}
        title={typeof value === 'string' ? value : undefined}
      >
        {loading ? (
          <span className="inline-block h-6 w-16 rounded bg-slate-100 animate-pulse" />
        ) : (
          value
        )}
      </div>
    </div>
  );
}

function EmptyState({ hasQuery }: { hasQuery: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border-2 border-dashed border-border bg-secondary/10 p-12 text-center">
      <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-slate-100 grid place-items-center">
        <Search className="h-5 w-5 text-slate-400" />
      </div>
      <p className="text-sm font-medium text-foreground">
        {hasQuery ? t('customer.noCustomersMatch') : t('customer.noCustomersYet')}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {hasQuery
          ? t('customer.tryDifferentSearch')
          : t('customer.clickNewCustomer')}
      </p>
    </div>
  );
}
